// Self-healing recovery for a BROKEN proxy engine. The updater (update.js) handles
// "a newer build exists"; this handles "the engine/transport is dead" even when the
// build id already matches - the stuck state that otherwise forces a manual
// Ctrl+Shift+R (stale service worker, stale bare-mux transport worker path, or a
// stale Scramjet config from a prior build).
//
// Triggers (all strong, engine-level signals):
//   - the engines never come up (navReady does not resolve) -> boot watchdog;
//   - boot throws (proxy.js catch);
//   - the bare-mux transport reports a dead port ("invalid MessagePort");
//   - a post-boot HEALTH SELF-TEST: we proxy one real request and, if the engine
//     returns a fast 5xx, we know the service worker is broken even though the boot
//     reported healthy (the "green status dot but pages will not proxy" case). This
//     is the signal a manual reload can't clear but a hard reload can.
//
// On any trigger, and only when the server is reachable (else reloading is futile),
// we converge onto a clean state, bounded to 2 attempts/session so it can never
// loop. User settings/favorites (localStorage) are always preserved.
//   plain reload : fresh no-cache HTML pulls the latest bundle + re-boots the engine
//                  (fixes a stale build / stale worker path).
//   clean reload : unregister the service worker + drop the caches (except the Study
//                  Helper's downloaded models) + clear the bare-mux worker path, then
//                  reload (a hard-reload equivalent - the fix for a broken SW that a
//                  plain reload keeps reusing).
import { get } from "../shared/settings-store.js";

const KEY = "rv:recover"; // sessionStorage: { n } - attempts so far this session
let busy = false;
let triggered = false; // a recovery was already started on THIS page-load

function read() { try { return JSON.parse(sessionStorage.getItem(KEY) || "{}"); } catch (e) { return {}; } }
function write(o) { try { sessionStorage.setItem(KEY, JSON.stringify(o)); } catch (e) {} }
function reset() { try { sessionStorage.removeItem(KEY); } catch (e) {} }

// The engine proved itself healthy (a real proxied request came back). Clear the
// escalation counter so a future, unrelated problem starts fresh (plain reload).
export function markHealthy() { reset(); }

export async function recoverProxy(reason, opts = {}) {
	if (busy || triggered) return;
	if (get("autoUpdate") === false) return; // respect the user's auto-reload opt-out
	triggered = true;
	busy = true;
	// Only reload if the server is reachable - a connectivity problem is the offline
	// UI's job, and reloading into no network just churns.
	let reachable = false;
	try { const r = await fetch("/__rv/version", { cache: "no-store", credentials: "omit" }); reachable = r.ok; } catch (e) {}
	if (!reachable) { busy = false; return; }

	const n = read().n || 0;
	if (n >= 2) { busy = false; return; } // already tried both; stop (never loop)
	// A clean reset on the 2nd attempt always, or on the 1st when the caller already
	// knows the service worker is the culprit (a healthy-boot 5xx).
	const clean = !!opts.clean || n >= 1;
	write({ n: n + 1, reason: reason || "" });
	window.__rvUpdating = true; // skip the "confirm close many tabs" beforeunload guard

	try {
		if (clean) {
			if (navigator.serviceWorker) {
				const regs = await navigator.serviceWorker.getRegistrations();
				await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
			}
			if (window.caches) {
				// Study Helper models (webllm/*) are big downloads and never the problem: keep them.
				const ks = (await caches.keys()).filter((k) => !k.startsWith("webllm/"));
				await Promise.all(ks.map((k) => caches.delete(k).catch(() => {})));
			}
			try { localStorage.removeItem("bare-mux-path"); } catch (e) {} // stale/pruned path = dead transport
		}
	} catch (e) {}
	location.reload();
}

// Proxy one real request through the engine to prove it actually works. A FAST 5xx
// means the engine itself errored (e.g. a stale SW config) despite a healthy boot -
// the exact "green dot but nothing proxies" failure - so we self-heal with a clean
// reload. A 2xx-4xx means the engine works (clear the counter). A timeout is
// ambiguous (slow network / target down) and is intentionally ignored.
let rv = null; // proxy.js's engine API, handed over by initRecovery (never a window global)
async function healthCheck() {
	let enc;
	try { enc = rv && rv.encode("https://example.com/"); } catch (e) { return; }
	if (!enc || enc.charAt(0) !== "/") return; // engine not ready to encode a proxied path
	let status = 0;
	try {
		const ac = new AbortController();
		const to = setTimeout(() => ac.abort(), 7000);
		const r = await fetch(enc, { signal: ac.signal, cache: "no-store", credentials: "omit" });
		clearTimeout(to);
		status = r.status;
		// On Standard (Scramjet 2) the engine chunk takes a 5xx (app/engines.js health5xx): a failed
		// connection reconnects, a failed relay hands new pages to Standard (v1).
		if (status >= 500 && rv && rv.health5xx && rv.health5xx(r)) return;
		try { if (r.body && r.body.cancel) r.body.cancel(); } catch (e) {}
	} catch (e) { return; } // aborted / network - ambiguous, do not act
	if (status >= 500) recoverProxy("healthcheck-5xx", { clean: true });
	else if (status > 0) markHealthy();
}

// Arm the watchdogs + the health self-test. `navReadyPromise` resolves once the
// engines are up; if it does not within bootMs they failed to load -> recover. Once
// the engines report up, run the health self-test to catch a broken-but-green SW.
export function initRecovery(navReadyPromise, opts = {}) {
	if (opts.api) rv = opts.api;
	const bootMs = opts.bootMs || 14000;
	const stableMs = opts.stableMs || 25000;
	let booted = false;
	try {
		navReadyPromise.then(() => { booted = true; setTimeout(healthCheck, 1500); }).catch(() => {});
	} catch (e) {}
	setTimeout(() => { if (!booted) recoverProxy("boot-timeout"); }, bootMs);
	setTimeout(() => { if (!triggered) reset(); }, stableMs);
}
